import { Ref, useEffect } from 'react'
import { Input } from 'antd'
import styled from 'styled-components'

import { usDateToFormDate } from 'admin/lib/dates'
import { FormItem, DateTimeUS } from 'admin/lib/Forms'
import { usDateFormatter } from 'admin/lib/Functions/timeZoneHelper'
import { UseFormReturn } from 'react-hook-form'

import { ConfidenceAndValidationsType } from 'admin/lib/Forms/ConfidenceAndValidationMappings'

export const DualTimeZoneDateTimePicker: React.FC<{
  id: string
  label: string
  name: string
  imperialName: string
  isImperial: boolean
  isDisabled?: boolean
  isHidden?: boolean
  form: UseFormReturn<Record<string, any>>
  firstInputRef?: Ref<HTMLElement> | null
  validateDate: (value: any) => boolean
  warningMessages: string[]
  onFocus?: () => void | null
  instrumented?: boolean
  confidenceAndValidation?: ConfidenceAndValidationsType | null
}> = ({
  id,
  label,
  name,
  imperialName,
  isImperial,
  isDisabled = false,
  isHidden = false,
  form,
  firstInputRef,
  validateDate,
  warningMessages,
  onFocus = null,
  instrumented = false,
  confidenceAndValidation = null
}) => {
  useEffect(() => {
    if (isImperial) {
      form.setValue(imperialName, usDateFormatter(form.getValues(name)))
    }
  }, [form, isImperial, name, imperialName])

  const handleUsDateBlur = (value: string) => {
    const formattedValue = usDateToFormDate(value)
    form.setValue(name, formattedValue)
    form.trigger(name)
  }

  return (
    <div
      className={
        warningMessages.length > 0 ? 'contains-validation-warning' : ''
      }
      style={isHidden ? { display: 'none' } : {}}
    >
      <FormItem
        label={label}
        name={name}
        id={id}
        inputRef={firstInputRef}
        rules={{
          validate: {
            checkValue: v => validateDate(v) || 'ERROR: Date is invalid'
          }
        }}
        disabled={isDisabled}
        onFocus={onFocus ?? undefined}
        instrumented={instrumented}
        confidenceAndValidation={!isImperial ? confidenceAndValidation : null}
      >
        <Input
          type="datetime-local"
          max="9999-12-31T23:59"
          style={isImperial ? { display: 'none' } : {}}
        />
      </FormItem>
      {isImperial && id && (
        // FormItem ID is automatically passed to the child component as a prop
        <FormItem
          id={id}
          label={<DateTimeUSLabel>(MM/DD/YYYY, hh:mm AM/PM)</DateTimeUSLabel>}
          name={imperialName}
          style={{
            marginTop: '-8px'
          }}
          disabled={isDisabled}
          confidenceAndValidation={isImperial ? confidenceAndValidation : null}
        >
          <DateTimeUS
            className="DateTimeUS"
            relatedFieldPath={name}
            handleDateBlur={handleUsDateBlur}
            disabled={isDisabled}
          />
        </FormItem>
      )}
      {warningMessages.map((warningMessage, index) => (
        <p key={index} className="validation-warning">
          {warningMessage}
        </p>
      ))}
    </div>
  )
}

export const DateTimeUSLabel = styled.div`
  font-size: 12px;
  color: #888888;
`
